/**
 * Assegnazione in blocco delle task di una sezione di progetto (logica pura).
 *
 * Stesso comportamento dell'assegnazione singola (TaskDocumentItem): le task
 * hanno UN solo assegnatario (tasks.assigned_to), quindi la persona scelta
 * SOSTITUISCE quella precedente e l'eventuale cliente del portale assegnato
 * viene tolto (assigned_client_access_id = null), come fa il singolo.
 */

export type SectionAssignScope = 'open' | 'all';

export interface AssignableTask {
  id: string;
  title?: string;
  section?: string | null;
  is_header?: boolean;
  status?: string;
  assigned_to?: string | null;
  assigned_client_access_id?: string | null;
}

export interface AssignableUser {
  id: string;
  full_name?: string | null;
  email?: string | null;
}

/** Task (non intestazioni) della sezione, eventualmente solo quelle aperte. */
export function getSectionTasksInScope<T extends AssignableTask>(
  tasks: T[],
  sectionId: string,
  scope: SectionAssignScope,
): T[] {
  return tasks.filter(t =>
    !t.is_header &&
    t.section === sectionId &&
    (scope === 'all' || t.status !== 'completed'),
  );
}

/**
 * Task da scrivere davvero: quelle che non sono gia' assegnate a quella persona
 * (o che hanno ancora un cliente del portale da togliere).
 */
export function getTasksToAssign<T extends AssignableTask>(
  tasksInScope: T[],
  userId: string,
): { toAssign: T[]; alreadyAssigned: number } {
  const toAssign = tasksInScope.filter(
    t => t.assigned_to !== userId || !!t.assigned_client_access_id,
  );
  return { toAssign, alreadyAssigned: tasksInScope.length - toAssign.length };
}

export function userDisplayName(user: AssignableUser | null | undefined): string {
  return (user?.full_name || user?.email || 'Utente').trim();
}

/** Filtro di ricerca del selettore: stesso criterio dell'assegnazione singola. */
export function filterUsersByQuery<U extends AssignableUser>(users: U[], query: string): U[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter(u =>
    (u.full_name || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q),
  );
}

export function buildConfirmMessage(count: number, sectionName: string, userName: string): string {
  return `Assegnare ${count} task di "${sectionName}" a ${userName}?`;
}

export function buildSuccessMessage(count: number, userName: string): string {
  return count === 1
    ? `1 task assegnata a ${userName}`
    : `${count} task assegnate a ${userName}`;
}

/** Testo della notifica unica per l'assegnatario (al posto di una per task). */
export function buildBulkNotificationMessage(
  senderName: string,
  count: number,
  sectionName: string | undefined,
  projectName: string | undefined,
): string {
  const quante = count === 1 ? '1 task' : `${count} task`;
  if (projectName) {
    const sezione = sectionName ? ` - ${sectionName}` : '';
    return `${senderName} ti ha assegnato ${quante} nel progetto ${projectName}${sezione}`;
  }
  const sezione = sectionName ? ` nella sezione ${sectionName}` : '';
  return `${senderName} ti ha assegnato ${quante}${sezione}`;
}

/** Aggiornamento locale immediato dello stato delle task. */
export function applyAssigneeLocally<T extends AssignableTask & { assignee?: unknown }>(
  tasks: T[],
  ids: string[],
  user: AssignableUser,
): T[] {
  const set = new Set(ids);
  return tasks.map(t =>
    set.has(t.id)
      ? {
          ...t,
          assigned_to: user.id,
          assigned_client_access_id: undefined,
          assignee: { id: user.id, email: user.email ?? null, full_name: user.full_name ?? null },
        }
      : t,
  );
}
